import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Box from "@mui/material/Box";
import Button from "designSystem/components/button/Button";
import FlexColumn from "designSystem/components/flex/FlexColumn";
import FlexRowCenter from "designSystem/components/flex/FlexRowCenter";
import { useLocale } from "designSystem/locale";
import PaneHeader from "designSystem/components/pane/PaneHeader";
import LoadingPlaceholder from "designSystem/components/placeholder/LoadingPlaceholder";
import Placeholder from "designSystem/components/placeholder/Placeholder";
import RequestErrorPlaceholder from "designSystem/components/placeholder/RequestErrorPlaceholder";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import { confirmLeavePage } from "csi/router";
import { useStore } from "csi/state/hooks";
import { observer } from "mobx-react-lite";
import React, { Fragment } from "react";
import CommentDetail from "./CommentDetail";
import CommentForm from "./CommentForm";
import CommentOutdatedBanner from "./CommentOutdatedBanner";
import CommentsList from "./CommentsList";
import CommentsPlaceholder from "./CommentsPlaceholder";
import SearchFilterHeader from "./SearchFilterHeader";
import { white } from "designSystem/styles/color";

const DeletedPlaceholder = () => (
	<Placeholder
		subtitleProps={{ res: "CommentDeletedSubtitle" }}
		titleProps={{ res: "CommentDeleted" }}
	>
		{{
			image:
				<Box color="grey.400" height={72}>
					<ChatBubbleOutlineIcon style={{ fontSize: 72 }} />
				</Box>,
		}}
	</Placeholder>
);

export const CommentsOnInstance = observer(() => {
	const loc = useLocale();
	const { commentsStore, rightPaneStore } = useStore();
	const { closeSelected, selected } = rightPaneStore;
	const {
		backToComments,
		fetchCommentUrls,
		isDisabledNewConversation,
		isError,
		isListStale,
		isLoading,
		isNewComment,
		isSelectedCommentDeleted,
		numberOfFilteredComments,
		resetError,
		selectedCommentName,
		startNewComment,
	} = commentsStore;

	const newCommentLabel = loc.resource("ConversationNew");
	const paneTitle = selectedCommentName || (isNewComment && newCommentLabel) || loc.resource(selected.id);

	const checkState = async (callback: () => void) => {
		if (commentsStore.isEditing) {
			try {
				await confirmLeavePage();
			} catch (ex) {
				return;
			}
		}
		callback();
	};

	let errorPlaceholder: JSX.Element;

	if (isError) {
		errorPlaceholder = <RequestErrorPlaceholder onRetry={resetError} />;
	} else if (isSelectedCommentDeleted) {
		errorPlaceholder = <DeletedPlaceholder />;
	}

	return (
		<FlexColumn>
			<PaneHeader
				back={!!selectedCommentName || isNewComment}
				onBack={() => checkState(backToComments)}
				onClose={() => checkState(closeSelected)}
				title={paneTitle}
			/>
			{errorPlaceholder ? (
				<FlexRowCenter
					flexGrow={1}
					justifyContent="center"
					px={3}
				>
					{errorPlaceholder}
				</FlexRowCenter>
			)
				: isNewComment ? (
					<Box overflow="auto" p={3}>
						<CommentForm />
					</Box>
				)
					: selectedCommentName ?
						<CommentDetail />
						: (
							<Fragment>
								<Box
									borderBottom={STANDARD_BORDER_STYLE}
									px={3}
									py={2}
								>
									<SearchFilterHeader />
								</Box>
								{isLoading || !numberOfFilteredComments ? (
									<FlexRowCenter
										flexGrow={1}
										justifyContent="center"
										px={3}
									>
										{isLoading ? (
											<LoadingPlaceholder
												subtitleProps={{ res: "LoadingInProgress", resParams: [loc.resource("Conversations")
													.toLocaleLowerCase()] }}
												titleProps={{ res: "Loading" }}
											/>
										)
											:
											<CommentsPlaceholder />
										}
									</FlexRowCenter>
								)
									: (
										<Fragment>
											{isListStale &&
										<CommentOutdatedBanner onClick={fetchCommentUrls} />
											}
											<CommentsList />
											<FlexRowCenter
												bgcolor={white[50]}
												borderTop={STANDARD_BORDER_STYLE}
												bottom={0}
												justifyContent="center"
												p={3}
												position="sticky"
											>
												<Button
													design="primary"
													disabled={isDisabledNewConversation}
													fullWidth
													onClick={startNewComment}
												>
													{newCommentLabel}
												</Button>
											</FlexRowCenter>
										</Fragment>
									)}
							</Fragment>
						)}
		</FlexColumn>
	);
});

export default CommentsOnInstance;